// Controls
import { DirectionalHint } from "@fluentui/react";
import { IconIndicator } from "./IconIndicator";
// Models
import { PriorityName } from "@ms/uno-models/lib/local/client/legacyPlanner/task/ITask";
// Style
import { useTheme } from "@fluentui/react";
import { getContextualMenuStyles } from "../../contextualMenu/ContextualMenu.styles";
import { getPriorityIndicatorStyles } from "./Indicators.styles";
// Utilities
import { ConvertPriorityNameToPriority, ConvertPriorityToPriorityName } from "@ms/uno-models/lib/local/client/legacyPlanner/utilities/ConversionUtilities";
import * as React from "react";
import { useLoggers } from "../../../hooks/useLoggers";
import { getPriorityIconProps, IconSize } from "../../../icons/IconUtilities";
/**
 * Priority Indicator component that renders an icon with the current priority of the task
 */ export const PriorityIndicator = (props)=>{
    const { priority, priorityStrings, taskPermissions, onUpdatePriority: updatePriority } = props;
    const { traceLogger } = useLoggers();
    const theme = useTheme();
    /**
     * Set the priority for the task
     */ const setPriority = React.useCallback((evt, item)=>{
        if (Object.values(PriorityName).includes(item.key)) {
            return updatePriority(ConvertPriorityNameToPriority(PriorityName[item.key]));
        }
    }, [
        updatePriority
    ]);
    const contextMenuItems = React.useMemo(()=>{
        return [
            {
                key: PriorityName[PriorityName.Urgent],
                disabled: !taskPermissions.canUpdatePriorityToUrgent,
                name: priorityStrings.priorityUrgentText,
                onClick: setPriority,
                iconProps: getPriorityIconProps(theme, PriorityName.Urgent, traceLogger)
            },
            {
                key: PriorityName[PriorityName.Important],
                name: priorityStrings.priorityImportantText,
                onClick: setPriority,
                iconProps: getPriorityIconProps(theme, PriorityName.Important, traceLogger)
            },
            {
                key: PriorityName[PriorityName.Medium],
                name: priorityStrings.priorityMediumText,
                onClick: setPriority,
                iconProps: getPriorityIconProps(theme, PriorityName.Medium, traceLogger)
            },
            {
                key: PriorityName[PriorityName.Low],
                name: priorityStrings.priorityLowText,
                onClick: setPriority,
                iconProps: getPriorityIconProps(theme, PriorityName.Low, traceLogger)
            }
        ];
    }, [
        priorityStrings,
        setPriority,
        taskPermissions,
        theme,
        traceLogger
    ]);
    const [priorityIconProps, priorityIconLabel] = React.useMemo(()=>{
        // We only care about the two visible priority icons Urgent and Important
        // Default to urgent and override below if Important
        let _priorityIconProps = getPriorityIconProps(theme, PriorityName.Urgent, traceLogger, IconSize.Small);
        let _priorityIconLabel = priorityStrings.priorityUrgentText;
        if (ConvertPriorityToPriorityName(priority) === PriorityName.Important) {
            _priorityIconProps = getPriorityIconProps(theme, PriorityName.Important, traceLogger, IconSize.Small);
            _priorityIconLabel = priorityStrings.priorityImportantText;
        }
        return [
            _priorityIconProps,
            _priorityIconLabel
        ];
    }, [
        priority,
        priorityStrings.priorityImportantText,
        priorityStrings.priorityUrgentText,
        theme,
        traceLogger
    ]);
    const styles = getPriorityIndicatorStyles(theme, ConvertPriorityToPriorityName(priority), taskPermissions.canUpdatePriority);
    return /*#__PURE__*/ React.createElement("div", {
        className: "priority indicator",
        title: priorityIconLabel
    }, /*#__PURE__*/ React.createElement(IconIndicator, {
        ...props,
        ariaLabel: priorityIconLabel,
        iconProps: priorityIconProps,
        disabled: !taskPermissions.canUpdatePriority,
        menuProps: {
            calloutProps: {
                className: "plannerAppCallout"
            },
            className: "contextualMenu",
            directionalHint: DirectionalHint.bottomAutoEdge,
            items: contextMenuItems,
            styles: getContextualMenuStyles(theme)
        },
        menuIconProps: {
            iconName: "",
            style: {
                display: "none"
            }
        },
        styles: styles
    }));
};
